/**
 * API service for Matchday Planning operations
 */

import {
  MatchdayPlanning,
  PlanningSegment,
  CreatePlanningRequest,
  CreateSegmentRequest,
  UpdateSegmentRequest,
  PlanningDatabaseStructure,
  VehicleAssignment,
  CreateVehicleAssignmentRequest,
  UpdateVehicleAssignmentRequest,
  StaffAssignment,
  CreateStaffAssignmentRequest,
  UpdateStaffAssignmentRequest,
  SegmentTemplate,
  PlanningTemplate,
  SaveSegmentFavoriteRequest,
  ApplyFavoriteRequest,
  CreatePlanningTemplateRequest,
  ApplyTemplateRequest,
  CopySegmentsFromPlanningRequest
} from '../types/matchdayPlanning';
import { getToken } from '../utils/auth';
import { getPartnerToken } from '../utils/partnerAuth';

// Resolve API base: use absolute VITE_API_BASE when provided; otherwise current origin.
const _configured = (import.meta as any).env?.VITE_API_BASE as string | undefined;
const _baseUrl = _configured && /^https?:\/\//i.test(_configured) ? _configured : window.location.origin;
const API_BASE_URL = `${_baseUrl}/api`;

/**
 * Get authentication token from localStorage
 * Checks both club token and partner token
 */
const getAuthToken = (): string | null => {
  const partnerToken = getPartnerToken();
  if (partnerToken) return partnerToken;
  return getToken();
};

/**
 * Create headers with authentication
 */
const createHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Transform enum values from PascalCase to lowercase
 */
const transformEnumsToLowercase = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => transformEnumsToLowercase(item));
  }

  const transformed: any = {};
  for (const key in obj) {
    const value = obj[key];

    if (key === 'anchorType' || key === 'vehicleType' || key === 'tripType') {
      transformed[key] = typeof value === 'string' ? value.toLowerCase() : value;
    } else if (typeof value === 'object' && value !== null) {
      transformed[key] = transformEnumsToLowercase(value);
    } else {
      transformed[key] = value;
    }
  }

  return transformed;
};

/**
 * Handle API response
 */
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return transformEnumsToLowercase(data);
};

/**
 * Planning API operations
 */
export const matchdayPlanningApi = {
  /**
   * Get all plannings
   */
  async getAllPlannings(): Promise<MatchdayPlanning[]> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning`, {
      method: 'GET',
      headers: createHeaders()
    });
    return handleResponse<MatchdayPlanning[]>(response);
  },

  /**
   * Get planning by ID
   */
  async getPlanningById(id: number): Promise<MatchdayPlanning> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/${id}`, {
      method: 'GET',
      headers: createHeaders()
    });
    return handleResponse<MatchdayPlanning>(response);
  },

  /**
   * Get planning by event (LocalMatchdayId or MatchId).
   * Returns null if no planning exists (expected, not an error).
   */
  async getPlanningByEvent(localMatchdayId?: number, matchId?: number): Promise<MatchdayPlanning | null> {
    const params = new URLSearchParams();
    if (localMatchdayId) params.append('localMatchdayId', localMatchdayId.toString());
    if (matchId) params.append('matchId', matchId.toString());

    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/by-event?${params.toString()}`, {
      method: 'GET',
      headers: createHeaders()
    });

    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`Failed to get planning: ${response.statusText}`);
    }

    const text = await response.text();
    if (!text || text.trim() === '' || text.trim() === 'null') return null;

    try {
      const data = JSON.parse(text);
      return data || null;
    } catch (error) {
      console.error('Error parsing planning response:', error, 'Response text:', text);
      return null;
    }
  },

  /**
   * Batch-load plannings by match IDs and/or local matchday IDs.
   * Returns all plannings that match any of the provided IDs in a single request.
   */
  async getPlanningsByEvents(matchIds: number[], localMatchdayIds?: number[]): Promise<MatchdayPlanning[]> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/batch`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ matchIds, localMatchdayIds })
    });
    return handleResponse<MatchdayPlanning[]>(response);
  },

  /**
   * Create new planning
   */
  async createPlanning(data: CreatePlanningRequest): Promise<MatchdayPlanning> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse<MatchdayPlanning>(response);
  },

  /**
   * Update planning
   */
  async updatePlanning(id: number, data: Partial<CreatePlanningRequest>): Promise<MatchdayPlanning> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/${id}`, {
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse<MatchdayPlanning>(response);
  },

  /**
   * Delete planning
   */
  async deletePlanning(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/${id}`, {
      method: 'DELETE',
      headers: createHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to delete planning: ${response.status}`);
    }
  },

  /**
   * Get segments for a planning
   */
  async getSegments(planningId: number): Promise<PlanningSegment[]> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/${planningId}/segments`, {
      method: 'GET',
      headers: createHeaders()
    });
    return handleResponse<PlanningSegment[]>(response);
  },

  /**
   * Create new segment
   */
  async createSegment(data: CreateSegmentRequest): Promise<PlanningSegment> {
    const toPascalCase = (str: string): string =>
      str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    const requestData = {
      ...data,
      scheduledTime: data.scheduledTime.includes(':') && data.scheduledTime.split(':').length === 2
        ? `${data.scheduledTime}:00`
        : data.scheduledTime,
      anchorType: toPascalCase(data.anchorType),
      vehicleType: toPascalCase(data.vehicleType),
      tripType: toPascalCase(data.tripType)
    };

    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/segments`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Segment creation failed:', response.status, errorText);
    }

    return handleResponse<PlanningSegment>(response);
  },

  /**
   * Update segment
   */
  async updateSegment(data: UpdateSegmentRequest): Promise<PlanningSegment> {
    const toPascalCase = (str: string): string =>
      str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

    const requestData = {
      ...data,
      scheduledTime: data.scheduledTime && data.scheduledTime.includes(':') && data.scheduledTime.split(':').length === 2
        ? `${data.scheduledTime}:00`
        : data.scheduledTime,
      anchorType: data.anchorType ? toPascalCase(data.anchorType) : undefined,
      vehicleType: data.vehicleType ? toPascalCase(data.vehicleType) : undefined,
      tripType: data.tripType ? toPascalCase(data.tripType) : undefined
    };

    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/segments/${data.id}`, {
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify(requestData)
    });
    return handleResponse<PlanningSegment>(response);
  },

  /**
   * Delete segment
   */
  async deleteSegment(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/segments/${id}`, {
      method: 'DELETE',
      headers: createHeaders()
    });
    if (!response.ok) {
      throw new Error(`Failed to delete segment: ${response.status}`);
    }
  },

  /**
   * Recalculate free segments
   */
  async recalculateSegments(planningId: number): Promise<PlanningSegment[]> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/${planningId}/recalculate`, {
      method: 'POST',
      headers: createHeaders()
    });
    return handleResponse<PlanningSegment[]>(response);
  },

  /**
   * Export planning data
   */
  async exportPlanning(planningId: number): Promise<PlanningDatabaseStructure> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/${planningId}/export`, {
      method: 'GET',
      headers: createHeaders()
    });
    return handleResponse<PlanningDatabaseStructure>(response);
  },

  /**
   * Import planning data
   */
  async importPlanning(data: PlanningDatabaseStructure): Promise<MatchdayPlanning> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/import`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse<MatchdayPlanning>(response);
  },

  // ============================================
  // Vehicle Assignment API
  // ============================================

  /**
   * Get all vehicle assignments for a planning
   */
  async getVehicleAssignments(planningId: number): Promise<VehicleAssignment[]> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/${planningId}/vehicle-assignments`, {
      headers: createHeaders()
    });
    return handleResponse<VehicleAssignment[]>(response);
  },

  /**
   * Create a new vehicle assignment
   */
  async createVehicleAssignment(request: CreateVehicleAssignmentRequest): Promise<VehicleAssignment> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/vehicle-assignments`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(request)
    });
    return handleResponse<VehicleAssignment>(response);
  },

  /**
   * Update a vehicle assignment
   */
  async updateVehicleAssignment(id: number, request: UpdateVehicleAssignmentRequest): Promise<VehicleAssignment> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/vehicle-assignments/${id}`, {
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify(request)
    });
    return handleResponse<VehicleAssignment>(response);
  },

  /**
   * Delete a vehicle assignment
   */
  async deleteVehicleAssignment(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/vehicle-assignments/${id}`, {
      method: 'DELETE',
      headers: createHeaders()
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to delete vehicle assignment');
    }
  },

  // ============================================
  // Staff Assignment Methods
  // ============================================

  /**
   * Get all staff assignments for a planning
   */
  async getStaffAssignments(planningId: number): Promise<StaffAssignment[]> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/${planningId}/staff-assignments`, {
      headers: createHeaders()
    });
    return handleResponse<StaffAssignment[]>(response);
  },

  /**
   * Create a new staff assignment
   */
  async createStaffAssignment(request: CreateStaffAssignmentRequest): Promise<StaffAssignment> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/staff-assignments`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(request)
    });
    return handleResponse<StaffAssignment>(response);
  },

  /**
   * Update a staff assignment
   */
  async updateStaffAssignment(id: number, request: UpdateStaffAssignmentRequest): Promise<StaffAssignment> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/staff-assignments/${id}`, {
      method: 'PUT',
      headers: createHeaders(),
      body: JSON.stringify(request)
    });
    return handleResponse<StaffAssignment>(response);
  },

  /**
   * Delete a staff assignment
   */
  async deleteStaffAssignment(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/MatchdayPlanning/staff-assignments/${id}`, {
      method: 'DELETE',
      headers: createHeaders()
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to delete staff assignment');
    }
  },

  // ============================================
  // Favorites API
  // ============================================

  async getFavorites(): Promise<SegmentTemplate[]> {
    const response = await fetch(`${API_BASE_URL}/Template/favorites`, {
      headers: createHeaders()
    });
    return handleResponse<SegmentTemplate[]>(response);
  },

  async saveFavorite(data: SaveSegmentFavoriteRequest): Promise<SegmentTemplate> {
    const response = await fetch(`${API_BASE_URL}/Template/favorites`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse<SegmentTemplate>(response);
  },

  async deleteFavorite(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/Template/favorites/${id}`, {
      method: 'DELETE',
      headers: createHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete favorite');
  },

  async applyFavorite(data: ApplyFavoriteRequest): Promise<PlanningSegment> {
    const response = await fetch(`${API_BASE_URL}/Template/favorites/apply`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse<PlanningSegment>(response);
  },

  // ============================================
  // Planning Templates API
  // ============================================

  async getPlanningTemplates(): Promise<PlanningTemplate[]> {
    const response = await fetch(`${API_BASE_URL}/Template/planning-templates`, {
      headers: createHeaders()
    });
    return handleResponse<PlanningTemplate[]>(response);
  },

  async createPlanningTemplate(data: CreatePlanningTemplateRequest): Promise<PlanningTemplate> {
    const response = await fetch(`${API_BASE_URL}/Template/planning-templates`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse<PlanningTemplate>(response);
  },

  async deletePlanningTemplate(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/Template/planning-templates/${id}`, {
      method: 'DELETE',
      headers: createHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete template');
  },

  async applyPlanningTemplate(data: ApplyTemplateRequest): Promise<MatchdayPlanning> {
    const response = await fetch(`${API_BASE_URL}/Template/planning-templates/apply`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse<MatchdayPlanning>(response);
  },

  // ============================================
  // Copy from Planning API
  // ============================================

  async copyFromPlanning(data: CopySegmentsFromPlanningRequest): Promise<MatchdayPlanning> {
    const response = await fetch(`${API_BASE_URL}/Template/copy-from-planning`, {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse<MatchdayPlanning>(response);
  }
};

export default matchdayPlanningApi;
